import { observable, action } from 'mobx';
import ApiCaller from '../utils/api-caller';
import { v4 } from 'uuid';

class FnbAdditionalItemStore {

    // Properties

    // Modal Create/Update Properties

    @observable showFnbAdditionalItemModal = false;
    @observable fnbAdditionalItemTitle = '';

    @observable tabOptionNameActiveKey = '1';
    @observable tabSubItemNameActiveKey = '1';

    @observable fnbAdditionalItems = [];
    @observable fnbAdditionalItemId = 0;

    @observable fnbTaskId = 0;
    @observable fnbTaskName = '';
    @observable isRequired = false;

    @observable optionName = '';
    @observable optionNameLangs = [];

    @observable controlType = 'check';
    @observable maxNumber = 0;

    @observable fnbAdditionalItemDetails = [];
    @observable fnbAdditionalItemDetailId = 0;

    @observable subItemName = '';
    @observable subItemNameLangs = [];

    @observable price = 0;

    @observable itemDetailTextButton = '+ Add Sub Item';
    @observable itemTextButton = 'Add Item';

    // Modal Search Item Properties

    @observable showFnbAdditinalItemSearchModal = false;
    @observable lastChildItems = [];
    @observable lastChildItemFilter = [];
    @observable itemSearchValue = '';

    // Helper Properties

    @observable fnbAdditionalItemListLoading = false;
    @observable fnbAdditionalItemModalLoading = false;

    // Initialization

    @action initFnbAdditionalItem() {
        this.fnbAdditionalItemListLoading = true;

        ApiCaller.FnbAdditionalItems.apiGetFnbAdditionalItems().then(fnbAdditionalItemResponses => {
            if (fnbAdditionalItemResponses) {
                this.fnbAdditionalItems = fnbAdditionalItemResponses;

                const languageCode = window.localStorage.getItem('lang') || 'en';
                ApiCaller.CmsTasks.getInRoomDiningAll(languageCode).then((fnbResponse) => {
                    if (fnbResponse) {
                        if (fnbResponse.items.length > 0) {
                            this.lastChildItems = fnbResponse.items;
                            this.lastChildItemFilter = this.lastChildItems;
                        }
                    }

                    this.fnbAdditionalItemListLoading = false;
                });
            } else {
                this.fnbAdditionalItems = [];
                this.fnbAdditionalItemListLoading = false;
            }
        });
    }

    @action initCreateFnbAdditionalItem(activeLanguages) {
        this.fnbAdditionalItemTitle = 'Create IRD Additional Item';

        this.fnbAdditionalItemId = 0;
        this.fnbTaskName = '';
        this.isRequired = false;

        this.optionName = '';

        if (activeLanguages) {
            const arrTemplate = activeLanguages.map(lang => {
                return {
                    id: 0,
                    langId: lang.id,
                    name: '',
                    description: ''
                }
            });

            this.optionNameLangs = [...arrTemplate]
            this.subItemNameLangs = [...arrTemplate]
        }

        this.controlType = 'check';
        
        if (this.controlType === 'check') {
            this.maxNumber = 3;
        } else {
            this.maxNumber = 0;
        }

        this.fnbAdditionalItemDetails = [];
        this.fnbAdditionalItemDetailId = 0;

        this.subItemName = '';

        this.price = 0;

        this.itemTextButton = 'Add Item';

        this.showFnbAdditionalItemModal = true;
    }

    @action initUpdateFnbAdditionalItem(fnbAdditionalItemId, activeLanguages) {
        this.fnbAdditionalItemModalLoading = true;
        this.fnbAdditionalItemTitle = 'Update IRD Additional Item';
        this.itemTextButton = 'Update Item';
        this.showFnbAdditionalItemModal = true;

        ApiCaller.FnbAdditionalItems.apiGetFnbAdditionalItemByFnbAdditionalItemId(fnbAdditionalItemId).then(fnbAdditionalItemResponse => {
            if (fnbAdditionalItemResponse) {
                this.fnbAdditionalItemId = fnbAdditionalItemResponse.fnbAdditionalItemId;
                this.fnbTaskId = fnbAdditionalItemResponse.fnbTaskId;
                this.fnbTaskName = fnbAdditionalItemResponse.fnbTaskName;
                this.isRequired = fnbAdditionalItemResponse.isRequired;

                this.optionName = fnbAdditionalItemResponse.optionName;
                this.optionNameLangs = fnbAdditionalItemResponse.optionNameLangs || [];

                this.subItemNameLangs = [];

                if (activeLanguages) {
                    activeLanguages.forEach(lang => {
                        const existingLang = this.optionNameLangs.find(item => item.langId === lang.id);
                        if (!existingLang) {
                            this.optionNameLangs.push({
                                id: 0,
                                langId: lang.id,
                                name: '',
                                description: ''
                            });
                        }

                        this.subItemNameLangs.push({
                            id: 0,
                            langId: lang.id,
                            name: '',
                            description: ''
                        })
                    });
                }

                this.controlType = fnbAdditionalItemResponse.controlType;
                this.maxNumber = fnbAdditionalItemResponse.maxNumber;

                this.fnbAdditionalItemDetails = fnbAdditionalItemResponse.itemDetails;
                this.fnbAdditionalItemDetailId = 0;

                this.subItemName = '';

                this.price = 0;

                this.itemTextButton = 'Update Item';

                this.fnbAdditionalItemModalLoading = false;
            }
        });
    }

    @action initUpdateFnbAdditionalItemDetail(fnbAdditionalItemDetailId, activeLanguages) {
        this.fnbAdditionalItemDetailId = fnbAdditionalItemDetailId;
        const fnbAdditionalItemDetail = this.fnbAdditionalItemDetails.find(fi => fi.fnbAdditionalItemDetailId === fnbAdditionalItemDetailId);

        const originalLangs = fnbAdditionalItemDetail?.subItemNameLangs || [];

        this.subItemNameLangs = activeLanguages.map(lang => {
            const existed = originalLangs.find(l => l.langId === lang.id);

            if (existed) {
                return { ...existed };
            }

            return {
                langId: lang.id,
                name: '',
                description: '',
            };
        });

        const englishLang = this.subItemNameLangs.find(l => l.langId === 1);
        this.subItemName = englishLang
            ? englishLang.name
            : (this.subItemNameLangs[0]?.name || '');

        if (fnbAdditionalItemDetail) {
            this.setPrice(fnbAdditionalItemDetail.price);
        } else {
            this.setPrice(0);
        }

        this.itemDetailTextButton = 'Update Item';
    }

    // TRANSACTION

    @action searchItem() {
        this.lastChildItemFilter = this.lastChildItems.filter(item => {
            let itemTaskName = item.taskName.toLowerCase();
            return itemTaskName.indexOf(this.itemSearchValue.toLowerCase()) !== -1;
        });
    }

    @action searchItemSelected(taskId) {
        const item = this.lastChildItems.find(x => x.taskId === taskId);
        this.fnbTaskId = taskId;
        this.fnbTaskName = item.taskName;
    }

    // Transaction Item

    @action createFnbAdditionalItem() {
        this.fnbAdditionalItemModalLoading = true;

        this.fnbAdditionalItemDetails.map(itemDetail => {
            itemDetail.fnbAdditionalItemDetailId = 0;
            return itemDetail;
        });

        const newItem = {
            'fnbAdditionalItemId': 0,
            'fnbTaskId': this.fnbTaskId,
            'fnbTaskName': this.fnbTaskName,
            'controlType': this.controlType,
            'maxNumber': this.maxNumber,
            'optionName': this.optionName,
            'optionNameLangs': this.optionNameLangs.slice(),
            'itemDetails': this.fnbAdditionalItemDetails,
            'isRequired': this.isRequired,
            'isDeleted': false
        };

        return ApiCaller.FnbAdditionalItems.postCreateV2AdditionalItemApi(newItem).then((createFnbAdditionalItemResponse) => {
            this.fnbAdditionalItemModalLoading = false;
            this.showFnbAdditionalItemModal = false;

            let isSuccess = false;

            if (createFnbAdditionalItemResponse) {
                if (!createFnbAdditionalItemResponse.isError) {
                    isSuccess = true;
                    this.initFnbAdditionalItem();
                }
            }

            return isSuccess;
        });
    }

    @action updateFnbAdditionalItem() {
        this.fnbAdditionalItemModalLoading = true;

        this.fnbAdditionalItemDetails.map(itemDetail => {
            itemDetail.fnbAdditionalItemDetailId = 0;
            itemDetail.fnbAdditionalItemId = this.fnbAdditionalItemId;
            return itemDetail;
        });

        const updateItem = {
            'fnbAdditionalItemId': this.fnbAdditionalItemId,
            'fnbTaskId': this.fnbTaskId,
            'fnbTaskName': this.fnbTaskName,
            'controlType': this.controlType,
            'maxNumber': this.maxNumber,
            'optionName': this.optionName,
            'optionNameLangs': this.optionNameLangs.slice(),
            'itemDetails': this.fnbAdditionalItemDetails,
            'isRequired': this.isRequired,
            'isDeleted': false
        };

        return ApiCaller.FnbAdditionalItems.putV2FnbAdditionalItemApi(this.fnbAdditionalItemId, updateItem).then((updateFnbAdditionalItemResponse) => {
            this.fnbAdditionalItemModalLoading = false;
            this.showFnbAdditionalItemModal = false;

            let isSuccess = false;

            if (updateFnbAdditionalItemResponse) {
                if (!updateFnbAdditionalItemResponse.isError) {
                    isSuccess = true;
                    this.initFnbAdditionalItem();
                }
            }

            return isSuccess;
        });
    }

    @action deleteUpdateFnbAdditionalItem(fnbAdditionalItemId) {
        this.fnbAdditionalItemListLoading = true;

        return ApiCaller.FnbAdditionalItems.apiDeleteFnbAdditionalItem(fnbAdditionalItemId).then((deleteFnbAdditionalItemResponse) => {
            this.fnbAdditionalItemListLoading = false;

            let isSuccess = false;

            if (deleteFnbAdditionalItemResponse) {
                if (!deleteFnbAdditionalItemResponse.isError) {
                    isSuccess = true;
                    this.initFnbAdditionalItem();
                }
            }

            return isSuccess;
        });
    }

    // Transaction Item Detail

    @action createOrUpdateFnbAdditionalItemDetail(activeLanguages) {
        const subItemNameLangsCopy = this.subItemNameLangs.map(l => ({...l}));

        if (this.fnbAdditionalItemDetailId === 0) {
            const newItemDetail = {
                'fnbAdditionalItemDetailId': v4(),
                'fnbAdditionalItemId': 0,
                'subItemName': this.subItemName,
                'subItemNameLangs': subItemNameLangsCopy,
                'price': this.price,
                'isDeleted': false
            };

            this.fnbAdditionalItemDetails.push(newItemDetail);
        } else {
            let fnbAdditionalItemDetail = this.fnbAdditionalItemDetails.find(fi => fi.fnbAdditionalItemDetailId === this.fnbAdditionalItemDetailId);
            fnbAdditionalItemDetail.subItemName = this.subItemName;
            fnbAdditionalItemDetail.subItemNameLangs = subItemNameLangsCopy;
            fnbAdditionalItemDetail.price = this.price;
        }

        this.fnbAdditionalItemDetailId = 0;

        if (activeLanguages) {
            this.subItemNameLangs = activeLanguages.map(lang => {
                return {
                    id: 0,
                    langId: lang.id,
                    name: '',
                    description: ''
                }
            });
        }

        this.subItemName = '';
        this.setPrice(0);

        this.itemDetailTextButton = '+ Add Sub Item';
    }

    @action deleteFnbAdditionalItemDetail(fnbAdditionalItemDetailId, activeLanguages) {
        this.fnbAdditionalItemDetails = this.fnbAdditionalItemDetails.filter(fi => fi.fnbAdditionalItemDetailId !== fnbAdditionalItemDetailId);
        this.fnbAdditionalItemDetailId = 0;

        if (activeLanguages) {
            this.subItemNameLangs = activeLanguages.map(lang => {
                return {
                    id: 0,
                    langId: lang.id,
                    name: '',
                    description: ''
                }
            });
        }
        this.setPrice(0);

        this.itemDetailTextButton = '+ Add Sub Item';
    }

    @action cancelFnbAdditionalItemDetail(activeLanguages) {
        this.fnbAdditionalItemDetailId = 0;

        if (activeLanguages) {
            this.subItemNameLangs = activeLanguages.map(lang => {
                return {
                    id: 0,
                    langId: lang.id,
                    name: '',
                    description: ''
                }
            });
        }
        this.setPrice(0);

        this.itemDetailTextButton = '+ Add Sub Item';
    }

    // SET Properties

    @action setShowFnbAdditionalItemModal(value) {
        this.showFnbAdditionalItemModal = value;
        if (!value) {
            this.tabOptionNameActiveKey = '1';
            this.tabSubItemNameActiveKey = '1';
        }
    }

    @action setShowFnbAdditinalItemSearchModal(value) {
        this.showFnbAdditinalItemSearchModal = value;
    }

    @action setItemSearchValue(value) {
        this.itemSearchValue = value
    }

    @action setOptionNameLang(langId, value) {
        let lang = this.optionNameLangs.find(l => l.langId === langId);
        if (lang) {
            lang.name = value;
        } else {
            this.optionNameLangs.push({ langId: langId, name: value, description: null });
        }

        if (langId === 1) {
            this.optionName = value;
        }
    }

    @action setIsRequired(value) {
        this.isRequired = value;
    }

    @action setControlType(value) {
        this.controlType = value;
    }

    @action setMaxNumber(value) {
        this.maxNumber = value;
    }

    @action setSubItemNameLang(langId, value) {
        let lang = this.subItemNameLangs.find(l => l.langId === langId);
        if (lang) {
            lang.name = value;
        } else {
            this.subItemNameLangs.push({ langId: langId, name: value, description: null });
        }

        if (langId === 1) {
            this.subItemName = value;
        }
    }

    @action setPrice(value) {
        this.price = value;
    }

    @action setTabOptionNameActiveKey(value) {
        this.tabOptionNameActiveKey = value;
    }

    @action setTabSubItemNameActiveKey(value) {
        this.tabSubItemNameActiveKey = value;
    }
}

export default new FnbAdditionalItemStore()